import React, { lazy, Suspense, useEffect, useState } from "react";
import { Route, Routes, useNavigate, useLocation } from "react-router-dom";
import Login from "./Pages/Login/Login";
import Home from "./Pages/Home/Home";
import Tac from "./Pages/TermsAndConditions/Tac";
import Privacy from "./Pages/PrivacyPolicy/Privacy";
import Help from "./Pages/Help/Help";
import Disclaimer from "./Pages/Disclaimer/Disclaimer";
import Whitepaper from "./Pages/Whitepaper/Whitepaper";
import AuthorizedComponent from "./AuthorizedComponentRoute";
import WalletInfo from "./Component/WalletInfo/WalletInfo";
import { useAppKitAccount } from "@reown/appkit/react";

const MapComponentMapbox = lazy(() => import("./Pages/Map/Map"));

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { address, isConnected } = useAppKitAccount();
  const [redirecting, setRedirecting] = useState(false);

  // Track page changes to detect potential loops
  useEffect(() => {
    // Reset redirect flags when page changes
    setRedirecting(false);
    
    // Check for redirect loop
    const now = Date.now();
    let recentNavigations = [];
    try {
      const stored = JSON.parse(sessionStorage.getItem("recent_navigations") || "[]");
      if (Array.isArray(stored)) recentNavigations = stored.filter(nav => nav && typeof nav.path === "string" && Number.isFinite(nav.time));
    } catch (_) {
      sessionStorage.removeItem("recent_navigations");
    }
    
    // Add current navigation
    recentNavigations.push({
      path: location.pathname,
      time: now
    });
    
    // Only keep last 10 navigations
    const recentOnly = recentNavigations.filter(nav => now - nav.time < 5000).slice(-10);
    sessionStorage.setItem("recent_navigations", JSON.stringify(recentOnly));
    
    // Detect loop: same path occurring 4+ times in last 5 seconds
    const pathCounts = {};
    recentOnly.forEach(nav => {
      pathCounts[nav.path] = (pathCounts[nav.path] || 0) + 1;
    });
    
    const inLoop = Object.values(pathCounts).some(count => count >= 4);
    
    if (inLoop) {
      console.log("NAVIGATION LOOP DETECTED! Breaking loop...");
      // Clear problematic session data to break the loop
      sessionStorage.removeItem("redirect_attempts");
      
      // If MetaMask is not connected but we have MetaMask login, clear that
      if (!isConnected) {
        console.log("Disconnected MetaMask detected. Clearing session to break loop.");
        sessionStorage.clear();
      }
    }
  }, [location.pathname, isConnected]);

  // Handle authentication state (wallet-only)
  useEffect(() => {
    if (redirecting) return;
    const publicPaths = [
      "/",
      "/login",
      "/help",
      "/terms",
      "/privacy",
      "/disclaimer",
      "/whitepaper",
    ];
    const isPublicPage = publicPaths.includes(location.pathname);
    if (!isConnected) {
      sessionStorage.clear();
      if (!isPublicPage) {
        setRedirecting(true);
        navigate("/login");
      }
    }
  }, [isConnected, address, navigate, location.pathname, redirecting]);

  return (
    <>
      {/* This component handles wallet authentication but doesn't render anything */}
      <WalletInfo />
      
      <Suspense fallback={<p role="status">Loading page…</p>}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="login" element={<Login />} />
        <Route
          path="map"
          element={
            <AuthorizedComponent>
              <MapComponentMapbox />
            </AuthorizedComponent>
          }
        />
        <Route path="terms" element={<Tac />} />
        <Route path="privacy" element={<Privacy />} />
        <Route path="help" element={<Help />} />
        <Route path="disclaimer" element={<Disclaimer />} />
        <Route path="whitepaper" element={<Whitepaper />} />
      </Routes>
      </Suspense>
    </>
  );
};

export default App;
